import { ListBoxViewModel, ValueFormField } from '@sitecore-jss/sitecore-jss-forms';
import { FieldChangeCallback, Label, ListFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import { FC, Fragment } from 'react';

import { FieldValidationErrors } from '../CustomFields/FieldValidationErrors';
import { getFormFieldAriaProps } from '../Functions/FormFieldAriaProps';

/**
 * Overrides the built-in Sitecore forms listbox.
 * @see [Original ListBox Implementation](https://github.com/Sitecore/jss/blob/v22.10.0/packages/sitecore-jss-react-forms/src/components/field-templates/list-box.tsx)
 *
 * Markup and functionality should be 1:1 with the exception of `getFormFieldAriaProps`.
 *
 * @param props - ListBox properties.
 * @returns A listbox field.
 */
export const BaseListBox: FC<ListFieldProps<ListBoxViewModel>> = (props) => {
  const { errors, field, onChange, tracker, value } = props;

  let finalValue: string | string[] = value;

  if (!field.model.multipleSelection) {
    finalValue = value[0];
  }

  return (
    <Fragment>
      <Label {...props} />
      <select
        className={field.model.cssClass}
        id={field.valueField.id}
        name={field.valueField.name}
        value={finalValue}
        size={field.model.rows}
        multiple={field.model.multipleSelection}
        onChange={(e) => handleOnChange(field, e.target.options, onChange)}
        onFocus={() => tracker.onFocusField(field, value)}
        onBlur={() => tracker.onBlurField(field, value, errors)}
        {...getFormFieldAriaProps(field, errors)}
      >
        {field.model.items.map((item) => (
          <option key={item.itemId} value={item.value}>
            {item.text}
          </option>
        ))}
      </select>
      <FieldValidationErrors {...props} />
    </Fragment>
  );
};

function handleOnChange(field: ValueFormField, options: HTMLOptionsCollection, callback: FieldChangeCallback) {
  const errorMessages = [];
  const newValues = [];
  let valid = true;

  for (let i = 0; i < options.length; i += 1) {
    if (options[i].selected) {
      newValues.push(options[i].value);
    }
  }

  if (field.model.required && newValues.length === 0) {
    errorMessages.push(`${field.model.title} is required`);
    valid = false;
  }

  callback(field.valueField.name, newValues, valid, errorMessages);
}
